import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { ReportState, ReportStep, ReportStepType, TaskStatus } from '../hooks/report/types';
import { ReportProgress, ReportItemData } from '../types/reportProgress';

type Action =
  | { type: 'SET_TASK_ID'; payload: string | null }
  | { type: 'SET_TASK_STATUS'; payload: TaskStatus | null }
  | { type: 'SET_REPORTS'; payload: ReportStep[] }
  | { type: 'SET_ANALYTICS_DATA'; payload: any }
  | { type: 'SET_CURRENT_STEP'; payload: ReportStepType }
  | { type: 'SET_IS_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SELECTED_REPORT_INDICES'; payload: number[] }
  | { type: 'SET_PROGRESS'; payload: ReportProgress | null }
  | { type: 'SET_PROGRESS_ITEMS'; payload: ReportItemData[] }
  | { type: 'UPDATE_PROGRESS_ITEM'; payload: ReportItemData }
  | { type: 'RESET_STATE' }
  | { type: 'UPDATE_ANALYTICS_STATE'; payload: {
      analyticsData: any;
      reports: ReportStep[];
      selectedReportIndices: number[];
      isLoading: boolean;
    }};

// Получаем taskId из разных источников
let savedTaskId = sessionStorage.getItem('report_task_id');
if (!savedTaskId) {
  savedTaskId = localStorage.getItem('report_task_id');
}
if (!savedTaskId) {
  try {
    const url = new URL(window.location.href);
    savedTaskId = url.searchParams.get('taskId');
  } catch (e) {
    console.error('Ошибка при получении taskId из URL:', e);
  }
}

// Получаем индексы выбранных отчетов
let savedIndices = sessionStorage.getItem('selected_report_indices');
if (!savedIndices) {
  savedIndices = localStorage.getItem('selected_report_indices');
}
const initialIndices = savedIndices ? JSON.parse(savedIndices) : [];

// Если нашли taskId, сохраняем его во все хранилища для надежности
if (savedTaskId) {
  localStorage.setItem('report_task_id', savedTaskId);
  sessionStorage.setItem('report_task_id', savedTaskId);
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('taskId', savedTaskId);
    window.history.replaceState({}, '', url);
  } catch (e) {
    console.error('Ошибка при сохранении taskId в URL:', e);
  }
}

const initialState: ReportState = {
  taskId: savedTaskId,
  taskStatus: null,
  reports: [],
  analyticsData: null,
  currentStep: 'init',
  isLoading: false,
  error: null,
  selectedReportIndices: initialIndices,
  progress: null,
  progressItems: []
};

function reducer(state: ReportState, action: Action): ReportState {
  switch (action.type) {
    case 'SET_TASK_ID':
      return { ...state, taskId: action.payload };
    case 'SET_TASK_STATUS':
      return { ...state, taskStatus: action.payload };
    case 'SET_REPORTS':
      return { ...state, reports: action.payload };
    case 'SET_ANALYTICS_DATA':
      return { ...state, analyticsData: action.payload };
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_IS_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_SELECTED_REPORT_INDICES':
      localStorage.setItem('selected_report_indices', JSON.stringify(action.payload));
      return { ...state, selectedReportIndices: action.payload };
    case 'SET_PROGRESS':
      return { ...state, progress: action.payload };
    case 'SET_PROGRESS_ITEMS':
      return { ...state, progressItems: action.payload };
    case 'UPDATE_PROGRESS_ITEM': {
      const updated = action.payload;
      const idx = state.progressItems.findIndex(item => item.index === updated.index);
      if (idx >= 0) {
        const newItems = [...state.progressItems];
        newItems[idx] = updated;
        return { ...state, progressItems: newItems };
      }
      return { ...state, progressItems: [...state.progressItems, updated] };
    }
    case 'UPDATE_ANALYTICS_STATE':
      return {
        ...state,
        analyticsData: action.payload.analyticsData,
        reports: action.payload.reports,
        selectedReportIndices: action.payload.selectedReportIndices,
        isLoading: action.payload.isLoading
      };
    case 'RESET_STATE':
      // Очищаем из localStorage
      localStorage.removeItem('report_task_id');
      localStorage.removeItem('selected_report_indices');
      localStorage.removeItem('selected_task_timestamp');
      
      // Очищаем из sessionStorage
      sessionStorage.removeItem('report_task_id');
      sessionStorage.removeItem('selected_report_indices');
      sessionStorage.removeItem('selected_task_timestamp');
      
      // Очищаем из URL
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('taskId');
        window.history.replaceState({}, '', url);
      } catch (e) {
        console.error('Ошибка при удалении taskId из URL:', e);
      }
      
      return initialState;
    default:
      return state;
  }
}

// Create context
type ReportStateContextType = {
  state: ReportState;
  setTaskId: (id: string | null) => void;
  setTaskStatus: (status: TaskStatus | null) => void;
  setReports: (reports: ReportStep[]) => void;
  setAnalyticsData: (data: any) => void;
  setCurrentStep: (step: ReportStepType) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedReportIndices: (indices: number[]) => void;
  setProgress: (progress: ReportProgress | null) => void;
  setProgressItems: (items: ReportItemData[]) => void;
  updateProgressItem: (item: ReportItemData) => void;
  updateAnalyticsState: (data: {
    analyticsData: any;
    reports: ReportStep[];
    selectedReportIndices: number[];
    isLoading: boolean;
  }) => void;
  resetState: () => void;
};

const ReportStateContext = createContext<ReportStateContextType | undefined>(undefined);

// Provider component
export const ReportStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setTaskId = useCallback((id: string | null) => {
    // Сохраняем taskId во все хранилища
    if (id) {
      // Сохраняем в localStorage
      localStorage.setItem('report_task_id', id);
      localStorage.setItem('selected_task_timestamp', new Date().toISOString());
      
      // Сохраняем в sessionStorage
      sessionStorage.setItem('report_task_id', id);
      sessionStorage.setItem('selected_task_timestamp', new Date().toISOString());
      
      // Сохраняем в URL
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('taskId', id);
        window.history.replaceState({}, '', url);
      } catch (e) {
        console.error('Ошибка при сохранении taskId в URL:', e);
      }
      
      console.log(`setTaskId - сохранен taskId во все хранилища: ${id}`);
    } else {
      // Если id равен null, очищаем все хранилища
      localStorage.removeItem('report_task_id');
      localStorage.removeItem('selected_task_timestamp');
      sessionStorage.removeItem('report_task_id');
      sessionStorage.removeItem('selected_task_timestamp');
      
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('taskId');
        window.history.replaceState({}, '', url);
      } catch (e) {
        console.error('Ошибка при удалении taskId из URL:', e);
      }
      
      console.log('setTaskId - очищен taskId из всех хранилищ');
    }
    
    // Обновляем состояние
    dispatch({ type: 'SET_TASK_ID', payload: id });
  }, []);

  const setTaskStatus = useCallback((status: TaskStatus | null) => {
    dispatch({ type: 'SET_TASK_STATUS', payload: status });
  }, []);

  const setReports = useCallback((reports: ReportStep[]) => {
    dispatch({ type: 'SET_REPORTS', payload: reports });
  }, []);

  const setAnalyticsData = useCallback((data: any) => {
    dispatch({ type: 'SET_ANALYTICS_DATA', payload: data });
  }, []);

  const setCurrentStep = useCallback((step: ReportStepType) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step });
  }, []);

  const setIsLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_IS_LOADING', payload: loading });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const setSelectedReportIndices = useCallback((indices: number[]) => {
    dispatch({ type: 'SET_SELECTED_REPORT_INDICES', payload: indices });
  }, []);

  const setProgress = useCallback((progress: ReportProgress | null) => {
    dispatch({ type: 'SET_PROGRESS', payload: progress });
  }, []);

  const setProgressItems = useCallback((items: ReportItemData[]) => {
    dispatch({ type: 'SET_PROGRESS_ITEMS', payload: items });
  }, []);

  const updateProgressItem = useCallback((item: ReportItemData) => {
    dispatch({ type: 'UPDATE_PROGRESS_ITEM', payload: item });
  }, []);

  const updateAnalyticsState = useCallback((data: {
    analyticsData: any;
    reports: ReportStep[];
    selectedReportIndices: number[];
    isLoading: boolean;
  }) => {
    dispatch({ type: 'UPDATE_ANALYTICS_STATE', payload: data });
  }, []);

  const resetState = useCallback(() => {
    dispatch({ type: 'RESET_STATE' });
  }, []);

  const value = {
    state,
    setTaskId,
    setTaskStatus,
    setReports,
    setAnalyticsData,
    setCurrentStep,
    setIsLoading,
    setError,
    setSelectedReportIndices,
    setProgress,
    setProgressItems,
    updateProgressItem,
    updateAnalyticsState,
    resetState
  };

  return (
    <ReportStateContext.Provider value={value}>
      {children}
    </ReportStateContext.Provider>
  );
};

// Hook to use the context
export const useReportStateContext = () => {
  const context = useContext(ReportStateContext);
  if (context === undefined) {
    throw new Error('useReportStateContext must be used within a ReportStateProvider');
  }
  return context;
};
